import { useCallback } from 'react';

import { useIntl } from 'react-intl-next';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import type RelayModernEnvironment from 'relay-runtime/lib/store/RelayModernEnvironment';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';

import {
	type OperationalEvent,
	useOperationalAnalytics,
} from '../analytics/use-operational-analytics';
import { type Edition, type Flag, type Product } from '../types';

import { type useOnAttemptToUpgradeQuery } from './__generated__/useOnAttemptToUpgradeQuery.graphql';
import { isProductOneOf } from './is-product-one-of';
import { getHamsKey } from './product-to-hams-key';

// Equates to ~4 minutes of attempts spread out between the delay of waiting for the response
export const MAX_ATTEMPTS = 48;
const TIME_BETWEEN_ATTEMPTS_MS = 5_000; // 5 seconds

type UpgradeState = {
	customerHasUpgraded: boolean;
	productHasUpgraded: boolean;
};

async function getHasJiraUpgradedFromFree(product: Product): Promise<UpgradeState> {
	// TODO: Add the query for GUPs to find out if the customer is on a manual trial
	const response = await fetch('/rest/internal/latest/meta/edition');

	if (!response.ok) {
		throw new Error(`Fetching upgrade status failed with ${response.status}`);
	}

	const data = (await response.json()) as Partial<
		Record<string, 'free' | 'standard' | 'premium' | unknown>
	>;

	const key = getHamsKey(product);

	const productHasUpgraded = data[key] === 'standard' || data[key] === 'premium';

	return {
		productHasUpgraded,
		customerHasUpgraded: false,
	};
}

async function getHasConfluenceUpgradedFromFree(
	environment: RelayModernEnvironment,
	entitlementId: string,
): Promise<UpgradeState> {
	const response = await fetchQuery<useOnAttemptToUpgradeQuery>(
		environment,
		graphql`
			query useOnAttemptToUpgradeQuery($entitlementId: ID!) {
				confluence_tenantContext {
					editions {
						confluence
					}
				}
				growthUnifiedProfile_getEntitlementProfile(entitlementId: $entitlementId) {
					lastKnownTrial {
						trialEndTimeStamp
						trialType
					}
				}
			}
		`,
		{
			entitlementId,
		},
	).toPromise();

	const confluenceEdition = response?.confluence_tenantContext?.editions?.confluence;
	const trialEndTimeStamp =
		response?.growthUnifiedProfile_getEntitlementProfile?.lastKnownTrial?.trialEndTimeStamp;
	const trialType = response?.growthUnifiedProfile_getEntitlementProfile?.lastKnownTrial?.trialType;

	const productHasUpgraded = Boolean(
		confluenceEdition && (confluenceEdition === 'STANDARD' || confluenceEdition === 'PREMIUM'),
	);

	const customerHasUpgraded = Boolean(
		trialEndTimeStamp && trialEndTimeStamp >= Date.now() && trialType === 'MANUAL_TRIAL',
	);

	return {
		productHasUpgraded,
		customerHasUpgraded,
	};
}

async function getHasProductUpgraded(
	product: Product,
	environment: RelayModernEnvironment,
	entitlementId: string,
): Promise<{
	customerHasUpgraded: boolean;
	productHasUpgraded: boolean;
}> {
	if (product === 'confluence') {
		return getHasConfluenceUpgradedFromFree(environment, entitlementId);
	}

	if (isProductOneOf(product, ['jira', 'jira-product-discovery', 'jira-service-management'])) {
		return getHasJiraUpgradedFromFree(product);
	}

	throw new Error(`Unsupported product ${product}`);
}

async function linearRetry<T>(
	fn: () => Promise<T>,
	sendAnalyticsEvent: (event: OperationalEvent) => void,
) {
	const sendAnalytics = (action: string, additionalAttributes: Record<string, unknown> = {}) => {
		sendAnalyticsEvent({
			action,
			actionSubject: 'pollForUpgrade',
			actionSubjectId: 'editionAwareness',
			attributes: {
				...additionalAttributes,
			},
		});
	};

	const waitForInterval = () =>
		new Promise((resolve) => setTimeout(resolve, TIME_BETWEEN_ATTEMPTS_MS));

	sendAnalytics('started');

	for (let attempts = 0; attempts < MAX_ATTEMPTS; attempts++) {
		try {
			const result = await fn();

			if (result) {
				sendAnalytics('success', { attempt: attempts + 1 });
				return true;
			}

			if (attempts < MAX_ATTEMPTS - 1) {
				await waitForInterval();
			}
		} catch (error) {
			sendAnalytics('error', {
				attempt: attempts + 1,
				error: error instanceof Error ? error.message : 'Unknown error',
			});

			if (attempts < MAX_ATTEMPTS - 1) {
				await waitForInterval();
			}
		}
	}

	sendAnalytics('timeout', { attempts: MAX_ATTEMPTS });

	return false;
}

export function useOnAttemptToUpgrade({
	product,
	edition,
	showFlag,
	hideFlag,
	entitlementId,
}: {
	edition: Edition;
	entitlementId: string;
	hideFlag?: (id: string) => void;
	product: Product;
	showFlag?: (flag: Flag) => void;
}) {
	const sendAnalyticsEvent = useOperationalAnalytics({ product, edition });
	const intl = useIntl();

	const environment = useRelayEnvironment();

	const showUpgradeInProgressFlag = useCallback(
		function () {
			if (product !== 'confluence') {
				// Currently we're only targeting confluence upgrades
				return;
			}

			const cohort = FeatureGates.getExperimentValue<string>(
				'manual_trial_w_pde_onboarding',
				'cohort',
				'not-enrolled',
			);

			sendAnalyticsEvent({
				action: 'showFlag',
				actionSubject: 'editionAwarenessUpgradePolling',
				actionSubjectId: 'upgradeInProgressFlag',
				attributes: {
					trackEventGrowthInitiative: 'transmuters',
				},
			});

			if (cohort === 'variation') {
				showFlag?.({
					id: '@atlassian/edition-awareness.upgrade-in-progress-flag',
					title: intl.formatMessage({
						id: '@atlassian/edition-awareness.upgrade-in-progress-flag.title',
						defaultMessage: 'Upgrading your plan',
						description: 'Flag title for informing the customer they are being upgraded',
					}),
					description: intl.formatMessage({
						id: '@atlassian/edition-awareness.upgrade-in-progress-flag.description',
						defaultMessage:
							'This may take a couple of minutes. We’ll let you know when it’s finished.',
						description: 'Upgrade in progress message',
					}),
				});
			}
		},
		[product, sendAnalyticsEvent, showFlag, intl],
	);

	const showUpgradedFlag = useCallback(
		function () {
			if (product !== 'confluence') {
				// Currently we're only targeting confluence upgrades
				return;
			}

			const cohort = FeatureGates.getExperimentValue<string>(
				'manual_trial_w_pde_onboarding',
				'cohort',
				'not-enrolled',
			);

			sendAnalyticsEvent({
				action: 'showFlag',
				actionSubject: 'editionAwarenessUpgradePolling',
				actionSubjectId: 'upgradeCompleteFlag',
				attributes: {
					trackEventGrowthInitiative: 'transmuters',
				},
			});

			if (cohort === 'variation') {
				hideFlag?.('@atlassian/edition-awareness.upgrade-in-progress-flag');

				showFlag?.({
					id: '@atlassian/edition-awareness.upgrade-complete-flag',
					title: intl.formatMessage({
						id: '@atlassian/edition-awareness.upgrade-complete-flag.title',
						defaultMessage: 'Refresh to start using new features',
						description:
							'Flag title for informing the customer they need to refresh the page to be on the new edition',
					}),
					description: intl.formatMessage({
						id: '@atlassian/edition-awareness.upgrade-complete-flag.description',
						// TODO: Add edition and product in here
						defaultMessage: 'Your upgrade is complete.',
						description: 'Upgrade complete message',
					}),
					actions: [
						{
							onClick: () => {
								window.location.reload();
							},
							content: intl.formatMessage({
								id: '@atlassian/edition-awareness.upgrade-complete-flag.action',
								defaultMessage: 'Refresh',
								description: 'Call to action to refresh the page',
							}),
						},
					],
				});
			}
		},
		[product, sendAnalyticsEvent, showFlag, hideFlag, intl],
	);

	const startCheckingForAttemptToUpgrade = useCallback(async () => {
		if (!fg('ea_upgrade_attempt_analytics')) {
			// Generic catch to ensure we don't run this if the gate is off
			return false;
		}

		let hasSeenInProgressFlag = false;

		return linearRetry(async () => {
			const { productHasUpgraded, customerHasUpgraded } = await getHasProductUpgraded(
				product,
				environment,
				entitlementId,
			);

			if (!productHasUpgraded && customerHasUpgraded && !hasSeenInProgressFlag) {
				// Customer has upgraded, but product does not reflect the change
				hasSeenInProgressFlag = true;
				showUpgradeInProgressFlag();
				return false;
			}

			if (productHasUpgraded) {
				showUpgradedFlag();
			}

			return productHasUpgraded;
		}, sendAnalyticsEvent);
	}, [
		environment,
		product,
		sendAnalyticsEvent,
		entitlementId,
		showUpgradedFlag,
		showUpgradeInProgressFlag,
	]);

	// At a later stage there will be a useEffect which checks if the customer has started a manual trial but is not on
	// a trial yet (aka in product doesn't reflect the action they performed). This polling will show a flag too

	return { startCheckingForAttemptToUpgrade };
}
